import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Trash2, KeyRound, User as UserIcon, ShieldCheck, Mail, CheckCircle2, Send, Unplug, CreditCard, Sparkles, ArrowRight, XCircle, Building2 } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

export default function Account() {
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState(user?.name || '');
  const [savingName, setSavingName] = useState(false);

  const [curPwd, setCurPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [newPwd2, setNewPwd2] = useState('');
  const [pwdLoading, setPwdLoading] = useState(false);

  // Gmail connection state
  const [gmail, setGmail] = useState({ connected: false, configured: false });
  const [gmailLoaded, setGmailLoaded] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  // Credits & billing
  const [credits, setCredits] = useState(null);
  const [payments, setPayments] = useState([]);
  const [cancelling, setCancelling] = useState(false);

  const loadBilling = useCallback(async () => {
    try {
      const [c, p] = await Promise.all([
        api.get('/account/credits'),
        api.get('/account/payments'),
      ]);
      setCredits(c.data);
      setPayments(p.data.items || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadBilling(); }, [loadBilling]);

  const cancelSubscription = async () => {
    if (!window.confirm('Cancel your monthly subscription? You will keep this month\'s credits, but no new ones will be added next cycle.')) return;
    setCancelling(true);
    try {
      await api.post('/payments/subscription/cancel');
      toast.success('Subscription cancellation scheduled');
      loadBilling();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not cancel');
    } finally {
      setCancelling(false);
    }
  };

  const loadGmail = useCallback(async () => {
    try {
      const { data } = await api.get('/account/gmail/status');
      setGmail(data);
    } catch { /* ignore */ }
    finally { setGmailLoaded(true); }
  }, []);

  useEffect(() => { loadGmail(); }, [loadGmail]);

  const connectGmail = async () => {
    try {
      const { data } = await api.get('/account/gmail/connect-url', { params: { origin: window.location.origin } });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start Gmail connect');
    }
  };

  const disconnectGmail = async () => {
    if (!window.confirm('Disconnect Gmail? You will not be able to send reports until reconnected.')) return;
    try {
      await api.delete('/account/gmail/disconnect');
      toast.success('Gmail disconnected');
      loadGmail();
    } catch {
      toast.error('Could not disconnect');
    }
  };

  const sendTestEmail = async () => {
    if (!testEmail) { toast.error('Enter an email to send to'); return; }
    try {
      const { data } = await api.post('/account/gmail/test-send', { to_email: testEmail });
      if (data.status === 'sent') toast.success(`Test email sent to ${testEmail}`);
      else toast.error(data.error || `Send returned: ${data.status}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    }
  };

  const isSuperAdmin = user?.role === 'super_admin';

  const saveName = async () => {
    setSavingName(true);
    try {
      await api.patch('/account/me', { name });
      await refresh();
      toast.success('Profile saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save');
    } finally {
      setSavingName(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (newPwd !== newPwd2) { toast.error('New passwords do not match'); return; }
    if (newPwd.length < 8) { toast.error('New password must be at least 8 characters'); return; }
    setPwdLoading(true);
    try {
      await api.post('/account/password', { current_password: curPwd, new_password: newPwd });
      toast.success('Password updated. Use the new password next sign-in.');
      setCurPwd(''); setNewPwd(''); setNewPwd2('');
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || 'Could not change password');
    } finally {
      setPwdLoading(false);
    }
  };

  const deleteAccount = async () => {
    if (!window.confirm('Permanently delete your account, all audits and sessions? This cannot be undone.')) return;
    try {
      await api.delete('/account/me');
      toast.success('Account deleted');
      await logout();
      nav('/', { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not delete');
    }
  };

  if (!user) return null;

  return (
    <Layout>
      <div className="mx-auto max-w-4xl px-4 sm:px-6 md:px-10 py-8 md:py-12">
        <div className="label-mono mb-2 flex items-center gap-2">
          {isSuperAdmin ? <ShieldCheck size={12} /> : <UserIcon size={12} />} Account / Settings
        </div>
        <h1 className="text-3xl md:text-4xl font-medium tracking-tight">{user.name || user.email}</h1>
        <p className="text-sm text-muted-foreground mt-2">
          <span className="font-mono uppercase tracking-widest">{user.role}</span> · <span>{user.email}</span>
        </p>

        {/* Profile */}
        <div className="mt-12 panel p-8 space-y-4" data-testid="profile-section">
          <div className="label-mono">Profile</div>
          <div>
            <Label className="label-mono-sm">Display name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-none mt-1 max-w-md" data-testid="account-name-input" />
          </div>
          <Button onClick={saveName} disabled={savingName} className="rounded-none" data-testid="save-profile-btn">
            {savingName ? 'Saving…' : 'Save profile'}
          </Button>
        </div>

        {/* Credits & Billing */}
        <div className="mt-8 panel p-8" data-testid="billing-section">
          <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
            <div>
              <div className="label-mono flex items-center gap-2"><CreditCard size={12} /> Credits &amp; Billing</div>
              <h2 className="text-xl font-medium mt-1">
                {credits?.total_active ?? '—'} {credits?.total_active === 1 ? 'audit credit' : 'audit credits'} available
              </h2>
              <p className="text-sm text-muted-foreground mt-2 max-w-xl">
                Credits are consumed when you run a PackCheck audit. Free signup credit lasts forever; monthly and annual bundles have expiry.
              </p>
            </div>
            <div className="flex items-center gap-2">
              {credits?.internal && (
                <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest border-gradient-pt px-2 py-1" data-testid="internal-badge">
                  <Building2 size={11} strokeWidth={1.8} /> Internal
                </span>
              )}
              <Link to="/pricing" className="inline-flex items-center gap-2 h-10 px-4 border border-foreground hover:bg-foreground hover:text-background transition-colors text-sm" data-testid="buy-more-credits">
                <Sparkles size={13} strokeWidth={1.5} /> Buy more
              </Link>
            </div>
          </div>

          {/* Active bundles */}
          {credits && credits.bundles?.length > 0 ? (
            <div className="border border-border" data-testid="credit-bundles-table">
              <div className="grid grid-cols-12 px-4 py-3 bg-surface label-mono">
                <div className="col-span-3">Type</div>
                <div className="col-span-2">Remaining</div>
                <div className="col-span-2">Granted</div>
                <div className="col-span-3">Expires</div>
                <div className="col-span-2">Purchased</div>
              </div>
              {credits.bundles.map((b) => (
                <div key={b.bundle_id} className="grid grid-cols-12 px-4 py-3 border-t border-border text-sm items-center" data-testid={`bundle-row-${b.type}`}>
                  <div className="col-span-3">
                    <div className="font-medium capitalize">{(b.type || '').replace(/_/g, ' ')}</div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5">{b.source || 'system'}</div>
                  </div>
                  <div className="col-span-2 font-mono text-base">{b.remaining}</div>
                  <div className="col-span-2 font-mono text-xs">{b.granted}</div>
                  <div className="col-span-3 font-mono text-xs">
                    {b.expires_at ? new Date(b.expires_at).toLocaleDateString() : 'Never'}
                  </div>
                  <div className="col-span-2 font-mono text-xs">
                    {b.purchased_at ? new Date(b.purchased_at).toLocaleDateString() : '—'}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="border border-border p-8 text-center" data-testid="no-credits">
              <div className="text-sm text-muted-foreground mb-4">No active credits. Buy a plan to start running audits.</div>
              <Link to="/pricing" className="inline-flex items-center gap-2 h-11 px-5 bg-foreground text-background hover:opacity-90 text-sm">
                View plans <ArrowRight size={14} strokeWidth={1.5} />
              </Link>
            </div>
          )}

          {/* Subscription status */}
          {credits?.razorpay_subscription_id && (
            <div className="mt-6 border border-border bg-surface p-4 flex items-center justify-between flex-wrap gap-3" data-testid="subscription-status">
              <div>
                <div className="label-mono mb-1">Monthly subscription</div>
                <div className="text-sm">Active · auto-renews on Razorpay</div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-1">{credits.razorpay_subscription_id}</div>
              </div>
              <Button onClick={cancelSubscription} disabled={cancelling} variant="outline" className="rounded-none" data-testid="cancel-subscription-btn">
                <XCircle size={14} strokeWidth={1.5} /> {cancelling ? 'Cancelling…' : 'Cancel subscription'}
              </Button>
            </div>
          )}

          {/* Payment history */}
          {payments.length > 0 && (
            <div className="mt-8" data-testid="payment-history">
              <div className="label-mono mb-3">Recent payments</div>
              <div className="border border-border scroll-table">
                <div className="grid grid-cols-12 px-4 py-3 bg-surface label-mono">
                  <div className="col-span-3">Date</div>
                  <div className="col-span-2">Plan</div>
                  <div className="col-span-2">Amount</div>
                  <div className="col-span-2">Status</div>
                  <div className="col-span-3">Reference</div>
                </div>
                {payments.map((p) => (
                  <div key={p.payment_id} className="grid grid-cols-12 px-4 py-3 border-t border-border text-sm items-center" data-testid={`payment-${p.payment_id}`}>
                    <div className="col-span-3 font-mono text-xs">{new Date(p.created_at).toLocaleString()}</div>
                    <div className="col-span-2 font-mono text-xs uppercase">{p.plan_id}</div>
                    <div className="col-span-2 font-mono">
                      ₹{(p.amount_inr || 0).toLocaleString('en-IN')}
                      {p.discount_inr > 0 && <span className="text-[10px] text-teal-500 ml-1">(-₹{p.discount_inr})</span>}
                    </div>
                    <div className="col-span-2 font-mono text-xs">
                      <span className={`uppercase ${p.status === 'paid' || p.status === 'active' ? 'text-gradient-pt' : 'text-muted-foreground'}`}>{p.status}</span>
                    </div>
                    <div className="col-span-3 font-mono text-[10px] text-muted-foreground truncate" title={p.order_id || p.subscription_id || ''}>{p.order_id || p.subscription_id || '—'}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Gmail connection — available to every user */}
        <div className="mt-8 panel p-8" data-testid="gmail-section">
          <div className="flex items-start justify-between mb-6">
            <div>
              <div className="label-mono flex items-center gap-2"><Mail size={12} /> Email delivery</div>
              <h2 className="text-xl font-medium mt-1">Send audit reports from your Gmail</h2>
              <p className="text-sm text-muted-foreground mt-2 max-w-xl">
                Connect a Gmail account to email completed audit reports to clients straight from PackCheck.
                Replies will land in your inbox, not ours.
              </p>
            </div>
            {gmail.connected ? (
              <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest border-gradient-pt px-2 py-1" data-testid="gmail-connected-pill">
                <CheckCircle2 size={11} strokeWidth={1.8} /> Connected
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground border border-border px-2 py-1" data-testid="gmail-disconnected-pill">
                Not connected
              </span>
            )}
          </div>

          {!gmail.configured && gmailLoaded && (
            <div className="border border-severity-high p-4 text-sm text-severity-high font-mono mb-6">
              Google OAuth is not configured on the server. Ask your admin to set GOOGLE_CLIENT_ID / GOOGLE_CLIENT_SECRET.
            </div>
          )}

          {gmail.connected ? (
            <>
              <div className="border border-border bg-surface p-5 mb-6" data-testid="gmail-status-card">
                <div className="grid grid-cols-3 gap-4 items-center">
                  <div>
                    <div className="label-mono mb-1">Sender</div>
                    <div className="text-sm">{gmail.sender_email}</div>
                  </div>
                  <div>
                    <div className="label-mono mb-1">Connected</div>
                    <div className="text-sm">{new Date(gmail.granted_at).toLocaleString()}</div>
                  </div>
                  <div className="text-right">
                    <Button onClick={disconnectGmail} variant="outline" className="rounded-none" data-testid="gmail-disconnect-btn">
                      <Unplug size={14} strokeWidth={1.5} /> Disconnect
                    </Button>
                  </div>
                </div>
              </div>
              <div className="border-t border-border pt-6">
                <div className="label-mono mb-2">Send a test email</div>
                <div className="flex gap-2">
                  <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="to@example.com" type="email" className="rounded-none flex-1 h-11" data-testid="gmail-test-email" />
                  <Button onClick={sendTestEmail} className="rounded-none h-11" data-testid="gmail-test-send">
                    <Send size={14} strokeWidth={1.5} /> Send test
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">If the test reaches your inbox, audit-report emails will too.</p>
              </div>
            </>
          ) : (
            <div className="space-y-4">
              <ol className="text-sm text-muted-foreground space-y-1 list-decimal pl-5">
                <li>Click <b>Connect Gmail</b> below.</li>
                <li>Sign in with the Gmail account you want PackCheck to send <em>from</em>.</li>
                <li>Grant the <code className="font-mono text-[11px]">gmail.send</code> permission.</li>
                <li>You'll return here automatically — that's it.</li>
              </ol>
              <Button onClick={connectGmail} disabled={!gmail.configured} className="rounded-none h-11" data-testid="gmail-connect-btn">
                <Mail size={14} strokeWidth={1.5} /> Connect Gmail
              </Button>
            </div>
          )}
        </div>

        {/* Password — only super-admin */}
        {isSuperAdmin && (
          <form onSubmit={changePassword} className="mt-8 panel p-8 space-y-4" data-testid="password-section">
            <div className="label-mono flex items-center gap-2"><KeyRound size={12} /> Change password</div>
            <p className="text-xs text-muted-foreground">Super-admin password rotation. Keep it strong — this is the only credential for the admin password-only login.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 max-w-2xl">
              <div>
                <Label className="label-mono-sm">Current</Label>
                <Input type="password" value={curPwd} onChange={(e) => setCurPwd(e.target.value)} className="rounded-none mt-1" data-testid="current-password" />
              </div>
              <div>
                <Label className="label-mono-sm">New</Label>
                <Input type="password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} className="rounded-none mt-1" data-testid="new-password" />
              </div>
              <div>
                <Label className="label-mono-sm">Confirm</Label>
                <Input type="password" value={newPwd2} onChange={(e) => setNewPwd2(e.target.value)} className="rounded-none mt-1" data-testid="confirm-password" />
              </div>
            </div>
            <Button type="submit" disabled={pwdLoading || !curPwd || !newPwd} className="rounded-none" data-testid="change-password-btn">
              {pwdLoading ? 'Updating…' : 'Update password'}
            </Button>
          </form>
        )}

        {/* Danger zone */}
        {!isSuperAdmin && user.email !== 'vrishali@thereforedesign.co.in' && (
          <div className="mt-8 panel p-8 border border-severity-high/60 space-y-4" data-testid="danger-section">
            <div className="label-mono text-severity-high">Danger zone</div>
            <p className="text-sm text-muted-foreground">Deleting your account will remove your profile, all your audits and active sessions immediately. This cannot be undone.</p>
            <Button onClick={deleteAccount} variant="outline" className="rounded-none border-severity-high text-severity-high hover:bg-severity-high hover:text-background" data-testid="delete-account-btn">
              <Trash2 size={14} strokeWidth={1.5} /> Delete account
            </Button>
          </div>
        )}
      </div>
    </Layout>
  );
}
